import {gsap} from '../vendors/gsap';
import appData from '../components/core/appData';
import isMobile from './isMobile';

const {getIsAppMounted, setIsAppMounted, getIsLoading, setIsLoading} = appData;

/**
 * This function is handling for page load animation
 *
 * @returns {Object} - methods to handle shutter animation
 */
const shutter = () => {
    const loadingScreen = document.querySelector('.loading-screen');
    const duration = 0.7;
    let shutterStatus = 'closed';

    /**
     * handles hide animation for mobile
     *
     * @param {Object} config - configuration props
     */
    const hideMobile = config => {
        const {onComplete: onCompleteCallback} = config ?? {};

        gsap.fromTo(
            loadingScreen,
            {height: '100dvh'},
            {
                height: '100%',
                ease: 'power1.inOut',
                duration,
                /**
                 * function to run on animation complete
                 */
                onComplete() {
                    setIsLoading(false);
                    onCompleteCallback?.();
                }
            }
        );
    };

    /**
     * handles hide animation for desktop
     *
     * @param {Object} config - configuration props
     */
    const hideDesktop = config => {
        const {onComplete: onCompleteCallback} = config ?? {};

        gsap.fromTo(
            loadingScreen,
            {height: '100dvh'},
            {
                height: '100%',
                ease: 'power1.inOut',
                duration,
                /**
                 * function to run on animation complete
                 */
                onComplete() {
                    setIsLoading(false);
                    onCompleteCallback?.();
                }
            }
        );
    };

    /**
     * handles show animation for mobile
     *
     * @param {Object} config - configuration props
     */
    const showMobile = config => {
        const {onComplete: onCompleteCallback} = config ?? {};

        gsap.fromTo(
            loadingScreen,
            {height: '100%'},
            {
                height: '100dvh',
                ease: 'power1.inOut',
                duration,
                /**
                 * function to run on animation complete
                 */
                onComplete() {
                    setIsLoading(false);
                    onCompleteCallback?.();
                }
            }
        );
    };

    /**
     * handles show animation for desktop
     *
     * @param {Object} config - configuration props
     * @param {boolean} [config.immediate] - if true jumps to final animation state
     * @param {Function} [config.onComplete] - on animation complete callback function
     */
    const showDesktop = config => {
        const {onComplete: onCompleteCallback} = config ?? {};

        gsap.fromTo(
            loadingScreen,
            {height: '100%'},
            {
                height: '100dvh',
                ease: 'power1.inOut',
                duration,
                /**
                 * function to run on animation complete
                 */
                onComplete() {
                    setIsLoading(false);
                    onCompleteCallback?.();
                }
            }
        );
    };

    /**
     * handles show animation
     *
     * @param {Object} config - configuration properties and methods
     */
    const show = config => {
        const {onComplete} = config ?? {};

        if (getIsLoading() || !getIsAppMounted()) return;

        setIsLoading(true);
        shutterStatus = 'open';

        if (isMobile()) {
            showMobile({onComplete});
        } else {
            showDesktop({onComplete});
        }
    };

    /**
     * handles hide animation
     *
     * @param {Object} config - configuration properties and methods
     * @param {boolean} [config.forceExec] - if true bypass conditions and execute the method
     * @param {Function} [config.onComplete] - on animation complete callback function
     */
    const hide = config => {
        const {onComplete, forceExec = false} = config ?? {};
        if (!forceExec && (getIsLoading() || !getIsAppMounted())) return;

        setIsLoading(true);
        shutterStatus = 'closed';

        if (isMobile()) {
            hideMobile({onComplete});
        } else {
            hideDesktop({onComplete});
        }

        // setting focus on newly displayed content
        document.querySelector('body')?.focus();
    };

    /**
     * Get the current status of the shutter: open or closed
     *
     * @returns {string} Returns open or closed
     */
    const getStatus = () => {
        return shutterStatus;
    };

    /**
     * handles animation on site load
     */
    const init = () => {
        gsap.set('.app-container', {autoAlpha: 1});

        /**
         *  executes hide animation on page load
         */
        window.onload = () => {
            hide({
                /**
                 * callback function to be executed on animation complete
                 */
                onComplete: () => {
                    setIsAppMounted();
                },
                forceExec: true
            });
        };
    };

    return {
        init,
        show,
        hide,
        getStatus
    };
};

export default shutter();
